import { useMemo } from 'react';
import { Layout } from '../../constants';
import useWindowSize from '../useWindowSize';

/**
 * Default minimum width estimates for table columns (in pixels)
 * These values represent reasonable minimum widths for different column types
 */
export const DEFAULT_COLUMN_MIN_WIDTHS = {
  /** Short text like status, dates */
  short: 100,
  /** Medium text like names */
  medium: 140,
  /** Long text like emails, comments */
  long: 180,
  /** Action buttons column */
  actions: 150,
} as const;

/**
 * Calculates the estimated minimum width needed for a table based on columns
 * @param columnCount Number of columns in the table (excluding actions)
 * @param hasActionsColumn Whether the table has an actions column
 * @param averageColumnWidth Average minimum width per column (default: 130px)
 * @returns Estimated minimum table width in pixels
 */
export const calculateTableMinWidth = (
  columnCount: number,
  hasActionsColumn: boolean = true,
  averageColumnWidth: number = 130
): number => {
  const contentWidth = columnCount * averageColumnWidth;
  const actionsWidth = hasActionsColumn ? DEFAULT_COLUMN_MIN_WIDTHS.actions : 0;
  const tablePadding = 40; // Table padding and cell spacing
  return contentWidth + actionsWidth + tablePadding;
};

/**
 * Calculates the minimum screen width needed for a table to display properly
 * considering the sidebar and layout padding
 * @param tableMinWidth Minimum width needed for the table content
 * @param includeSidebar Whether to account for sidebar width
 * @returns Minimum screen width in pixels
 */
export const calculateRequiredScreenWidth = (tableMinWidth: number, includeSidebar: boolean = true): number => {
  const sidebarWidth = includeSidebar ? Layout.SIDEBAR_DESKTOP_WIDTH : 0;
  const layoutPadding = 60; // General layout padding
  return tableMinWidth + sidebarWidth + layoutPadding;
};

export interface TableResponsiveConfig {
  /** Number of columns (excluding actions) */
  columnCount: number;
  /** Whether table has an actions column */
  hasActionsColumn?: boolean;
  /** Average minimum width per column (pixels) */
  averageColumnWidth?: number;
  /** Custom column widths for precise calculation */
  columnWidths?: number[];
  /** Extra padding/margin to account for */
  extraPadding?: number;
}

export interface UseTableResponsiveResult {
  /** Whether the mobile view should be shown */
  showMobileView: boolean;
  /** Current window width */
  windowWidth: number;
  /** Calculated minimum width needed for the table */
  requiredWidth: number;
  /** Available width for the table content (excluding sidebar) */
  availableWidth: number;
}

/**
 * Hook to determine if a table should display in mobile or desktop view
 * based on the actual content requirements rather than fixed breakpoints.
 *
 * This is particularly useful for tables with many columns where the standard
 * breakpoints may not be appropriate.
 *
 * @example
 * // For a table with 6 columns + actions
 * const { showMobileView } = useTableResponsive({ columnCount: 6 });
 *
 * @example
 * // With custom column widths for precise calculation
 * const { showMobileView } = useTableResponsive({
 *   columnCount: 6,
 *   columnWidths: [110, 110, 140, 180, 100, 150], // Specific widths per column
 * });
 */
const useTableResponsive = (config: TableResponsiveConfig): UseTableResponsiveResult => {
  const { width: windowWidth } = useWindowSize();

  const result = useMemo(() => {
    const { columnCount, hasActionsColumn = true, averageColumnWidth = 130, columnWidths, extraPadding = 0 } = config;

    // Calculate table minimum width
    let tableMinWidth: number;

    if (columnWidths && columnWidths.length > 0) {
      // Use provided column widths for precise calculation
      tableMinWidth = columnWidths.reduce((sum, width) => sum + width, 0);
      if (hasActionsColumn) {
        tableMinWidth += DEFAULT_COLUMN_MIN_WIDTHS.actions;
      }
      tableMinWidth += 40; // Table padding
    } else {
      // Use average column width estimation
      tableMinWidth = calculateTableMinWidth(columnCount, hasActionsColumn, averageColumnWidth);
    }

    tableMinWidth += extraPadding;

    // Calculate required screen width
    const requiredWidth = calculateRequiredScreenWidth(tableMinWidth, true);

    // Calculate available width (window minus sidebar and padding)
    const availableWidth = windowWidth - Layout.SIDEBAR_DESKTOP_WIDTH - 60;

    // Show mobile view if screen is too narrow for the content
    const showMobileView = windowWidth < requiredWidth || windowWidth === 0;

    return {
      showMobileView,
      windowWidth,
      requiredWidth,
      availableWidth,
    };
  }, [windowWidth, config]);

  return result;
};

export default useTableResponsive;
